import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { timeRangeOpToJSON } from '@/proto/logiblocks/logiblocks';
import { ConditionOutcome } from '@/proto/logiblocks/node_run_report';
import { getDayOfWeekShortLabel } from '../dateTimeUtils';
import {
  formatChipDateLabel,
  formatEntryTimeLabel,
  timeRangeToWindow,
} from '../nodes/conditional/CustomDateTimeDayPicker/dateSetUtils';
import { variableRefName } from '../variables/variableUtils';
import {
  getEvaluatedAt,
  getRecordedEntries,
  type DateTimeSetCondition,
  type RecordedEntry,
} from './conditionalReportUtils';
import styles from './RunNodeAttemptsDrawer.module.css';

/** Returns `evaluatedAt` as "Dec 30, 2026, 1:00 PM", in its own zone. */
function formatEvaluatedAt(evaluatedAt: Dayjs): string {
  return evaluatedAt.format('MMM D, YYYY, h:mm A');
}

/** Returns the label the date/time set editor shows for `entry`, such as "Dec 30" or "Mon". */
function formatEntryName(entry: RecordedEntry): string {
  if ('date' in entry) {
    return formatChipDateLabel(entry.date);
  }
  return getDayOfWeekShortLabel(entry.day);
}

/** Sets up to this size are listed in full; bigger ones start collapsed. */
const INLINE_ENTRY_LIMIT = 4;

/** Returns `entries` in the order the date/time set editor lists them: dates ascending, and
 * weekdays from Sunday. */
function sortEntries(entries: RecordedEntry[]): RecordedEntry[] {
  return [...entries].sort((a, b) => {
    if ('date' in a && 'date' in b) {
      return a.date.localeCompare(b.date);
    }
    if ('day' in a && 'day' in b) {
      return a.day - b.day;
    }
    // A set's entries are all dates or all weekdays, because `DateTimeSetReport.entries` is a
    // oneof.
    return 0;
  });
}

/** Returns whether `entry` falls on the day of `evaluatedAt`, in `evaluatedAt`'s zone. */
function isOnEvaluatedDay(
  entry: RecordedEntry,
  evaluatedAt: Dayjs | null
): boolean {
  if (evaluatedAt === null) {
    return false;
  }
  if ('date' in entry) {
    return entry.date === evaluatedAt.format('YYYY-MM-DD');
  }
  // `Dayjs.day()` and `DayOfWeek` both number Sunday 0 through Saturday 6.
  return entry.day === evaluatedAt.day();
}

/** Lists set entries with their time windows, highlighting those on the evaluated day. */
function EntryRows({
  entries,
  evaluatedAt,
}: {
  entries: RecordedEntry[];
  evaluatedAt: Dayjs | null;
}) {
  return (
    <div className={styles.entryList}>
      {entries.map((entry) => (
        <div
          key={'date' in entry ? entry.date : entry.day}
          className={
            isOnEvaluatedDay(entry, evaluatedAt)
              ? `${styles.entryRow} ${styles.entryRowMatch}`
              : styles.entryRow
          }
        >
          <span>{formatEntryName(entry)}</span>
          <span className={styles.entryWindow}>
            {formatEntryTimeLabel(timeRangeToWindow(entry.time_range))}
          </span>
        </div>
      ))}
    </div>
  );
}

/** The set a condition was checked against, as it stood at run time. */
function CheckedSet({
  entries,
  entryCount,
  evaluatedAt,
}: {
  entries: RecordedEntry[];
  entryCount: number;
  evaluatedAt: Dayjs | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const sorted = sortEntries(entries);
  const isDateSet = 'date' in entries[0];
  const unit = isDateSet ? 'dates' : 'days';
  const cutCount = entryCount - entries.length;
  const small = sorted.length <= INLINE_ENTRY_LIMIT && cutCount <= 0;
  const onEvaluatedDay = sorted.filter((e) => isOnEvaluatedDay(e, evaluatedAt));

  return (
    <div className={styles.checkedSet}>
      <div className={styles.fieldLabel}>
        Checked against {entryCount} {unit}
      </div>
      {small ? (
        <EntryRows entries={sorted} evaluatedAt={evaluatedAt} />
      ) : (
        <>
          <div className={styles.fieldValue}>
            {onEvaluatedDay.length > 0
              ? `Includes the evaluated ${isDateSet ? 'date' : 'day'}`
              : `Does not include the evaluated ${isDateSet ? 'date' : 'day'}`}
          </div>
          {onEvaluatedDay.length > 0 && (
            <EntryRows entries={onEvaluatedDay} evaluatedAt={evaluatedAt} />
          )}
          <button
            type='button'
            className={styles.linkButton}
            aria-expanded={expanded}
            onClick={() => setExpanded((open) => !open)}
          >
            {expanded ? `Hide all ${unit}` : `Show all ${entryCount} ${unit}`}
          </button>
          {expanded && <EntryRows entries={sorted} evaluatedAt={evaluatedAt} />}
          {expanded && cutCount > 0 && (
            <div className={styles.fieldLabel}>
              and {cutCount} more not recorded
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Returns "Result: true" or "Result: false" for a checked condition, or null if it wasn't checked. */
function formatOutcome(outcome: ConditionOutcome): string | null {
  switch (outcome) {
    case ConditionOutcome.CONDITION_OUTCOME_PASSED:
      return 'Result: true';
    case ConditionOutcome.CONDITION_OUTCOME_FAILED:
      return 'Result: false';
    case ConditionOutcome.CONDITION_OUTCOME_NOT_EVALUATED:
      return null;
    case ConditionOutcome.CONDITION_OUTCOME_UNSPECIFIED:
    case ConditionOutcome.UNRECOGNIZED:
    default:
      console.error(`Condition report has no valid outcome: ${outcome}`);
      return null;
  }
}

/** Shows how one date/time set condition evaluated against its set. */
function ConditionRow({ condition }: { condition: DateTimeSetCondition }) {
  const { expr, conditionReport, dateTimeSetReport } = condition;
  const variableName =
    expr.variable === undefined ? '' : variableRefName(expr.variable);
  const operator = timeRangeOpToJSON(expr.operator)
    .toLowerCase()
    .replace(/_/g, ' ');
  // An unresolved set's report has no name, so the label uses the name the graph saved.
  const setName = dateTimeSetReport.resolved
    ? dateTimeSetReport.name
    : expr.display_label;
  const evaluatedAt = getEvaluatedAt(condition);
  const result = formatOutcome(conditionReport.outcome);
  const entries = getRecordedEntries(dateTimeSetReport);
  return (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>
        {[variableName, operator, setName]
          .filter((part) => part !== '')
          .join(' ')}
      </div>
      {evaluatedAt !== null && (
        <div className={styles.fieldValue}>
          {`Evaluated at ${formatEvaluatedAt(evaluatedAt)} (${dateTimeSetReport.iana_timezone})`}
        </div>
      )}
      {result !== null && <div className={styles.fieldValue}>{result}</div>}
      {entries.length > 0 && (
        <CheckedSet
          entries={entries}
          entryCount={dateTimeSetReport.entry_count}
          evaluatedAt={evaluatedAt}
        />
      )}
      {!dateTimeSetReport.resolved && (
        <div className={`${styles.fieldValue} ${styles.errorValue}`}>
          This condition needs review: it could not be resolved and was treated
          as not matching.
        </div>
      )}
    </div>
  );
}

/** Shows how each date/time set condition in a Conditional node evaluated during one run. */
export function ConditionalAttemptDetail({
  conditions,
}: {
  conditions: DateTimeSetCondition[];
}) {
  return (
    <>
      {conditions.map((condition, index) => (
        <ConditionRow
          // eslint-disable-next-line react/no-array-index-key -- conditions have no ids; a run's report lists them in graph order and never reorders them
          key={index}
          condition={condition}
        />
      ))}
    </>
  );
}
